import {
  Button,
  Hidden,
  IconButton,
  MenuItem,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Switch,
  FormControlLabel,
} from "@material-ui/core";

import { Edit, Delete, Check, Clear, Add } from "@material-ui/icons";
import React, { useEffect, useState } from "react";
import NoItems from "./NoItems";
import styles from "./ConfigVariableList.module.css";
import { Controller, useForm } from "react-hook-form";
import {
  $ServiceConfigVar,
  ApiError,
  Project,
  ProjectRoute,
} from "../_generated";
import { useHistory } from "react-router-dom";
import { validationFromSchema } from "../util";
import ConfirmDialog from "./ConfirmDialog";

/*
interface ConfigVar {
  readonly pk?: number;
  service?: number;
  environment: number;
  name: string;
  value: string;
  readonly date_created?: string;
  readonly date_updated?: string;
}*/

export default function RoutingList(props: {
  routes: ProjectRoute[];
  project: Project;
  serviceNames?: Map<number, string>;
  onVariableCreate: (configVar: ProjectRoute) => Promise<any>;
  onVariableUpdate: (configVar: ProjectRoute) => Promise<any>;
  onVariableDelete: (id: number) => Promise<any>;
}) {
  const history = useHistory();
  const [rowEditing, setRowEditing] = useState(-1);
  const [rowPending, setRowPending] = useState(-1);
  const [confirmID, setConfirmID] = useState(-1);
  const [newRow, setNewRow] = useState(false);

  useEffect(() => {
    let unblock;
    if (rowEditing !== -1) {
      // TODO: replace with a prettier dialog (using window.prompt now)
      unblock = history.block(
        "Unsaved changes. If you leave the pages your changes will be lost."
      );
    }
    return unblock;
  }, [history, rowEditing]);

  const onSubmit = async (data: ProjectRoute) => {
    setRowPending(data.pk!);
    try {
      await props.onVariableUpdate(data);
      setRowEditing(-1);
    } catch (e) {
      handleError(e);
    }
    setRowPending(-1);
  };

  const onDeleteConfirm = async (id: number) => {
    setConfirmID(-1);
    try {
      await props.onVariableDelete(id);
    } catch (e) {
      handleError(e);
    }
  };

  const onCreate = async (data: ProjectRoute) => {
    setRowPending(-2);
    try {
      await props.onVariableCreate(data);
      setNewRow(false);
    } catch (e) {
      handleError(e);
    }
    setRowPending(-1);
  };

  const handleError = (e: ApiError) => {
    // TODO: show error to the user
    console.error(e);
  };

  return props.routes.length || newRow ? (
    <>
      <TableContainer component={Paper} variant="outlined">
        <Table className={styles.variablesTable}>
          <TableHead>
            <TableRow>
              <TableCell>Routing Prefix</TableCell>
              <Hidden xsDown>
                <TableCell>Service</TableCell>
                <TableCell>Allow CORS?</TableCell>
              </Hidden>
              <TableCell></TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {[...props.routes]
              .sort(byServiceAndName)
              .map((route) =>
                rowEditing === route.pk ? (
                  <ConfigVarRowEdit
                    route={route}
                    serviceNames={props.serviceNames}
                    onSubmit={onSubmit}
                    onReset={() => setRowEditing(-1)}
                    disabled={route.pk === rowPending}
                    key={route.pk}
                  />
                ) : (
                  <ConfigVarRow
                    route={route}
                    serviceNames={props.serviceNames}
                    onEditClick={() => setRowEditing(route.pk!)}
                    onDeleteClick={() => setConfirmID(route.pk!)}
                    key={route.pk}
                  />
                )
              )}
            {newRow ? (
              <ConfigVarRowEdit
                route={{
                  project: props.project.pk || -1,
                  service: 1,
                  route_prefix: "/",
                  allow_cors: true,
                }}
                serviceNames={props.serviceNames}
                onReset={() => setNewRow(false)}
                onSubmit={onCreate}
                disabled={rowPending === -2}
              ></ConfigVarRowEdit>
            ) : (
              <TableRow>
                <TableCell colSpan={5} align="right">
                  <Button startIcon={<Add />} onClick={() => setNewRow(true)}>
                    Add route
                  </Button>
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </TableContainer>
      <ConfirmDialog
        open={confirmID !== -1}
        id={confirmID}
        onCancel={() => setConfirmID(-1)}
        onConfirm={(id) => onDeleteConfirm(id)}
        text="Do you really want to delete this route?"
      />
    </>
  ) : (
    <NoItems>
      <Button startIcon={<Add />} onClick={() => setNewRow(true)}>
        Add Route
      </Button>
    </NoItems>
  );
}

const byServiceAndName = (a: ProjectRoute, b: ProjectRoute) =>
  a.service === b.service
    ? byName(a, b)
    : a.route_prefix! > b.route_prefix!
    ? 1
    : -1;

const byName = (a: ProjectRoute, b: ProjectRoute) =>
  a.route_prefix === b.route_prefix
    ? 0
    : a.route_prefix > b.route_prefix
    ? 1
    : -1;

function ConfigVarRow(props: {
  route: ProjectRoute;
  serviceNames: Map<number, string> | undefined;
  onEditClick: () => void;
  onDeleteClick: () => void;
}) {
  const route = props.route;
  return (
    <TableRow>
      {props.serviceNames ? <TableCell>{route.route_prefix}</TableCell> : null}
      {props.serviceNames ? (
        <TableCell component="th" scope="row">
          {route.service
            ? props.serviceNames.get(route.service)
            : "not defined"}
        </TableCell>
      ) : null}
      <Hidden xsDown>
        <TableCell>{route.allow_cors ? "Yes" : "No"}</TableCell>
      </Hidden>
      <TableCell align="right" className={styles.actionColumn}>
        <IconButton onClick={props.onEditClick}>
          <Edit />
        </IconButton>
        <IconButton onClick={props.onDeleteClick} edge="end">
          <Delete />
        </IconButton>
      </TableCell>
    </TableRow>
  );
}

function ConfigVarRowEdit(props: {
  route: ProjectRoute;
  serviceNames: Map<number, string> | undefined;
  disabled: boolean;
  onSubmit: (route: ProjectRoute) => void;
  onReset: () => void;
}) {
  const { register, handleSubmit, control, errors } = useForm();

  function onSubmit(data: any) {
    if (!data.route_prefix.endsWith("/")) {
      data.route_prefix = data.route_prefix + "/";
    }
    console.log(data.route_prefix);
    props.onSubmit({
      ...data,
      pk: props.route.pk,
      service: props.serviceNames
        ? data.service !== -1
          ? data.service
          : undefined
        : props.route.service,
    });
  }

  return (
    <TableRow>
      <TableCell component="th" scope="row">
        <TextField
          name="route_prefix"
          variant="outlined"
          label="Prefix"
          defaultValue={props.route.route_prefix}
          inputRef={register(
            validationFromSchema($ServiceConfigVar.properties.name)
          )}
          error={errors.name !== undefined}
          helperText={errors.name?.message}
          fullWidth
          disabled={props.disabled}
        />
      </TableCell>
      {props.serviceNames ? (
        <TableCell>
          <Controller
            control={control}
            name="service"
            defaultValue={props.route.service ?? -1}
            render={({ onChange, value, ref }) => (
              <TextField
                select
                variant="outlined"
                label="Service"
                value={value}
                inputRef={ref}
                onChange={onChange}
                fullWidth
                disabled={props.disabled}
              >
                {Array.from(props.serviceNames!).map(([pk, name]) => (
                  <MenuItem key={pk} value={pk}>
                    {name}
                  </MenuItem>
                ))}
              </TextField>
            )}
          />
        </TableCell>
      ) : null}
      <TableCell component="th" scope="row">
        <FormControlLabel
          control={
            <Switch
              name="allow_cors"
              defaultChecked={props.route.allow_cors}
              inputRef={register}
            />
          }
          label="Allow CORS"
        />
      </TableCell>
      <TableCell className={styles.actionColumn}></TableCell>
      <TableCell align="right" className={styles.actionColumn}>
        <IconButton
          role="submit"
          onClick={handleSubmit(onSubmit)}
          disabled={props.disabled}
        >
          <Check />
        </IconButton>
        <IconButton
          role="cancel"
          onClick={props.onReset}
          disabled={props.disabled}
          edge="end"
        >
          <Clear />
        </IconButton>
      </TableCell>
    </TableRow>
  );
}
